import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { CrewCertCountKey, CrewCertItem, CrewCertPerson, CrewCerts as CrewCertsData } from '@/api/types'
import { OUTCOME_LABEL, STATE_LABEL, badgeClass, dayLabel, outcomeTone, stateTone } from '@/lib/certs'

/**
 * My crew's certifications — status only, read-only (design §7).
 *
 * One card per person, worst first: the person a supervisor needs to talk to
 * leads. Tap to see what they hold. No card numbers, providers or notes for
 * anyone, and medical cards show only whether they are in order — a
 * supervisor needs to know someone may wear a respirator, not the clearance
 * behind it.
 *
 * Floaters (on nobody's crew) are behind a toggle: any supervisor may record
 * them, so may need to check them, but there are about fifty.
 */

const COUNT_ORDER: { key: CrewCertCountKey; label: string; tone: 'error' | 'warn' | 'ok' }[] = [
  { key: 'Missing', label: 'missing', tone: 'error' },
  { key: 'Expired', label: 'expired', tone: 'error' },
  { key: 'Blocked', label: 'on hold', tone: 'error' },
  { key: 'Expiring', label: 'expiring', tone: 'warn' },
  { key: 'Pending', label: 'unchecked', tone: 'warn' },
  { key: 'Valid', label: 'good', tone: 'ok' },
]

function itemLine(i: CrewCertItem): { text: string; cls: string } {
  if (i.requirement_outcome === 'Missing') {
    return { text: 'Missing — required', cls: badgeClass('error') }
  }
  if (i.cert_state) {
    const date = i.expires_date ? ` · ${i.cert_state === 'Expired' ? 'expired' : 'until'} ${dayLabel(i.expires_date)}` : ''
    return { text: `${STATE_LABEL[i.cert_state]}${date}`, cls: badgeClass(stateTone(i.cert_state)) }
  }
  if (i.requirement_outcome) {
    return { text: OUTCOME_LABEL[i.requirement_outcome], cls: badgeClass(outcomeTone(i.requirement_outcome)) }
  }
  return { text: '', cls: 'badge' }
}

function PersonCard({ p }: { p: CrewCertPerson }) {
  const [open, setOpen] = useState(false)
  const problems = p.counts.Missing + p.counts.Expired + p.counts.Blocked
  return (
    <div className="card">
      <button
        className="btn btn-secondary btn-block"
        style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ textAlign: 'left' }}>
          {p.display_name}
          {p.is_self && <span className="muted" style={{ fontWeight: 400 }}> (you)</span>}
        </span>
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {COUNT_ORDER.filter((c) => p.counts[c.key] > 0).map((c) => (
            <span key={c.key} className={badgeClass(c.tone)}>
              {p.counts[c.key]} {c.label}
            </span>
          ))}
          {p.items.length === 0 && <span className="badge">nothing on file</span>}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {p.items.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No certifications on file and none required.</p>
          ) : (
            p.items.map((i) => {
              const v = itemLine(i)
              return (
                <div key={i.cert_type_id}
                     style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0',
                              borderTop: '1px solid var(--c-border)' }}>
                  <span>
                    {i.type_name}
                    {i.is_restricted && <span className="muted" style={{ fontSize: '0.8125rem' }}> · medical</span>}
                  </span>
                  <span className={v.cls} style={{ whiteSpace: 'nowrap' }}>{v.text}</span>
                </div>
              )
            })
          )}
          {problems > 0 && (
            <p className="muted" style={{ margin: '8px 0 0', fontSize: '0.875rem' }}>
              The office records and renews cards — send {p.is_self ? 'yours' : 'theirs'} in.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export function CrewCerts() {
  const [data, setData] = useState<CrewCertsData | null>(null)
  const [floaters, setFloaters] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await api.crewCerts(floaters))
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? "Certifications aren't switched on for you yet."
          : err instanceof ApiError && err.status === 403
            ? 'This screen is for supervisors.'
            : "Couldn't load your crew. Check your signal and try again.",
      )
    }
  }, [floaters])

  useEffect(() => {
    void load()
  }, [load])

  const attention = (data?.people ?? []).filter(
    (p) => p.counts.Missing + p.counts.Expired + p.counts.Blocked > 0,
  ).length

  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0, marginBottom: 4 }}>Crew certifications</h2>
        <p className="muted" style={{ margin: 0 }}>
          {data
            ? `${data.people.length} ${data.people.length === 1 ? 'person' : 'people'} · ${attention} need attention · as of ${dayLabel(data.as_of)}`
            : 'You and your crew.'}
        </p>
      </div>

      {error && (
        <div className="alert alert-warn">
          {error}
          {error.includes('signal') && (
            <button className="btn btn-secondary btn-block" style={{ marginTop: 12 }} onClick={() => void load()}>
              Try again
            </button>
          )}
        </div>
      )}
      {!data && !error && <p className="muted">Loading…</p>}

      {data?.people.map((p) => <PersonCard key={p.employee_id} p={p} />)}

      {data && (
        <label className="checkline card" style={{ fontWeight: 400 }}>
          <input type="checkbox" checked={floaters} onChange={(e) => setFloaters(e.target.checked)} />{' '}
          Also show people on no crew (floaters)
        </label>
      )}
      {floaters && data && (
        <>
          <h3 style={{ margin: '8px 0 0' }}>On no crew</h3>
          {data.floaters.length === 0 ? (
            <p className="muted">Nobody.</p>
          ) : (
            data.floaters.map((p) => <PersonCard key={p.employee_id} p={p} />)
          )}
        </>
      )}

      <p className="muted" style={{ fontSize: '0.875rem' }}>
        Read only. Medical certifications show only whether they are in order.
      </p>
    </div>
  )
}
